import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";
import { getActorName, formatPopularity } from "@/utils/content";
import FavoriteButton from "@/components/feature/FavoriteButton";

interface DetailHeroProps {
  drama: Drama;
  onFeedback: (message: string) => void;
}

export default function DetailHero({ drama, onFeedback }: DetailHeroProps) {
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: drama.title, text: drama.synopsis, url });
        onFeedback("分享成功，感谢你的推荐！");
      } else {
        await navigator.clipboard.writeText(url);
        onFeedback("页面链接已复制，快分享给朋友吧");
      }
    } catch {
      onFeedback("分享已取消");
    }
  };

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <img
        src={drama.banner}
        alt={`${drama.title} 剧照`}
        title={`${drama.title} 剧情亮点`}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/70 to-black/45"></div>

      <div className="relative mx-auto flex w-full max-w-[1280px] flex-col gap-5 px-4 py-8 md:flex-row md:gap-8 md:px-6 md:py-12">
        <div className="h-56 w-40 shrink-0 overflow-hidden rounded-lg bg-background-800 md:h-72 md:w-52">
          <img
            src={drama.cover}
            alt={`${drama.title} 海报`}
            title={`${drama.title} 剧集介绍与解析`}
            className="h-full w-full object-top"
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/category?cat=${drama.category}`}
              className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              {drama.category}
            </Link>
            <span className="rounded-full bg-white/15 px-3 py-1 text-xs text-white">{drama.genre}</span>
            {drama.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/80">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-2xl font-bold text-white md:text-4xl">{drama.title}</h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white/85">
            <span className="flex items-center gap-1 text-accent-300">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-star-fill"></i></span>
              {drama.rating} 分
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-fire-line"></i></span>
              热度 {formatPopularity(drama.popularity)}
            </span>
            <span>{drama.year}</span>
            <span>{drama.region}</span>
            <span>{drama.episodes > 1 ? `${drama.episodes}集 · ${drama.duration}` : drama.duration}</span>
          </div>

          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-white/80 md:text-base">{drama.synopsis}</p>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
            <span>
              导演：<span className="text-white">{drama.director}</span>
            </span>
            <span>
              编剧：<span className="text-white">{drama.writer}</span>
            </span>
            <span>
              上线：<span className="text-white">{drama.publishDate}</span>
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
            主演：
            {drama.cast.map((id) => (
              <Link
                key={id}
                to={`/actor/${id}`}
                className="rounded-full bg-white/10 px-3 py-1 text-xs text-white transition-colors hover:bg-white/20"
              >
                {getActorName(id)}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <FavoriteButton dramaId={drama.id} dramaTitle={drama.title} onFeedback={onFeedback} />
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10"
            >
              <span className="flex h-4 w-4 items-center justify-center"><i className="ri-share-forward-line"></i></span>
              分享
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}